/**
 * OrgSettingsPanel — Floating panel for editing the current organization
 * (Phase 4). Mirrors TeamPanel's idiom: bottom-right floating card opened from
 * the EditorPage left rail. Editor-adjacent settings stay as panels rather than
 * a separate /settings route, per project preference.
 *
 * Editable: name (owner/admin), logo URL (owner/admin), slug (owner only —
 * changing slug breaks any outstanding invite URLs).
 * Read-only: plan, status, created date, member count.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../services/apiFetch';
import { useAuthStore, type OrgSummary } from '../../stores/authStore';
import { useBillingStore } from '../../stores/billingStore';
import './OrgSettingsPanel.css';

interface OrgRecord {
  id: string;
  name: string;
  slug: string;
  logo_url?: string | null;
  plan_id?: string | null;
  status?: string | null;
  created_at?: string | null;
}

interface OrgSettingsPanelProps {
  onClose: () => void;
}

export default function OrgSettingsPanel({ onClose }: OrgSettingsPanelProps) {
  const currentOrg = useAuthStore((s) => s.currentOrg);
  const subscription = useBillingStore((s) => s.subscription);
  const createPortal = useBillingStore((s) => s.createPortal);
  const createCheckout = useBillingStore((s) => s.createCheckout);

  const isOwner = currentOrg?.role === 'owner';
  const canEdit = isOwner || currentOrg?.role === 'admin';
  const canBill = canEdit; // owner+admin only

  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  const [org, setOrg] = useState<OrgRecord | null>(null);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  const refresh = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    setError(null);
    try {
      const [record, members] = await Promise.all([
        apiFetch<OrgRecord>(`/api/v2/organizations/${currentOrg.id}`),
        apiFetch<unknown[]>(`/api/v2/organizations/${currentOrg.id}/members`).catch(() => [] as unknown[]),
      ]);
      setOrg(record);
      setName(record.name ?? '');
      setSlug(record.slug ?? '');
      setLogoUrl(record.logo_url ?? '');
      setMemberCount(Array.isArray(members) ? members.length : null);
    } catch (err) {
      setError(messageFromError(err, 'Failed to load organization'));
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const dirty =
    !!org &&
    (name.trim() !== (org.name ?? '') ||
      slug.trim() !== (org.slug ?? '') ||
      logoUrl.trim() !== (org.logo_url ?? ''));

  const validate = (): string | null => {
    const trimmedName = name.trim();
    if (trimmedName.length < 2 || trimmedName.length > 100) {
      return 'Name must be 2–100 characters';
    }
    if (isOwner) {
      const trimmedSlug = slug.trim();
      if (trimmedSlug.length < 2) return 'Slug must be at least 2 characters';
      if (!/^[a-z0-9-]+$/.test(trimmedSlug)) {
        return 'Slug can only contain lowercase letters, numbers, and hyphens';
      }
    }
    if (logoUrl.trim() && !/^https?:\/\//i.test(logoUrl.trim())) {
      return 'Logo URL must start with http:// or https://';
    }
    return null;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !org || !canEdit) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const body: Record<string, unknown> = {};
    const trimmedName = name.trim();
    const trimmedLogo = logoUrl.trim();
    const trimmedSlug = slug.trim();

    if (trimmedName !== (org.name ?? '')) body.name = trimmedName;
    if (trimmedLogo !== (org.logo_url ?? '')) body.logo_url = trimmedLogo;
    if (isOwner && trimmedSlug !== (org.slug ?? '')) body.slug = trimmedSlug;

    if (Object.keys(body).length === 0) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await apiFetch<OrgRecord>(`/api/v2/organizations/${currentOrg.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setOrg(updated);
      setName(updated.name ?? '');
      setSlug(updated.slug ?? '');
      setLogoUrl(updated.logo_url ?? '');
      pushOrgUpdateToAuthStore(updated);
      setSuccess('Saved');
      window.setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(messageFromError(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!org) return;
    setName(org.name ?? '');
    setSlug(org.slug ?? '');
    setLogoUrl(org.logo_url ?? '');
    setError(null);
  };

  const handleManageSubscription = async () => {
    if (!canBill || billingBusy) return;
    setBillingBusy(true);
    setBillingError(null);
    try {
      const url = await createPortal();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setBillingError(messageFromError(err, 'Failed to open billing portal'));
    } finally {
      setBillingBusy(false);
    }
  };

  const handleUpgrade = async () => {
    if (!canBill || billingBusy) return;
    setBillingBusy(true);
    setBillingError(null);
    try {
      const url = await createCheckout('professional');
      window.location.href = url;
    } catch (err) {
      setBillingError(messageFromError(err, 'Failed to start checkout'));
      setBillingBusy(false);
    }
  };

  return (
    <div className="org-settings-panel">
      <header className="org-settings-panel__header">
        <span className="org-settings-panel__title">
          Organization
          {currentOrg && <span className="org-settings-panel__org">· {currentOrg.name}</span>}
        </span>
        <button
          type="button"
          className="org-settings-panel__header-btn"
          onClick={onClose}
          aria-label="Close organization settings"
        >
          ×
        </button>
      </header>

      <div className="org-settings-panel__body">
        {!currentOrg && (
          <div className="org-settings-panel__placeholder">
            You're not currently a member of any organization.
          </div>
        )}

        {error && <div className="org-settings-panel__alert org-settings-panel__alert--error">{error}</div>}
        {success && (
          <div className="org-settings-panel__alert org-settings-panel__alert--success">{success}</div>
        )}

        {currentOrg && loading && !org && (
          <div className="org-settings-panel__placeholder">Loading…</div>
        )}

        {currentOrg && org && (
          <>
            <form onSubmit={handleSave} className="org-settings-panel__form">
              <label className="org-settings-panel__field">
                <span className="org-settings-panel__label">Name</span>
                <input
                  type="text"
                  className="org-settings-panel__input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit || saving}
                  maxLength={100}
                  required
                />
              </label>

              <label className="org-settings-panel__field">
                <span className="org-settings-panel__label">
                  Slug
                  {!isOwner && (
                    <span className="org-settings-panel__hint"> (owner only)</span>
                  )}
                </span>
                <input
                  type="text"
                  className="org-settings-panel__input"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={!isOwner || saving}
                  pattern="[a-z0-9-]+"
                  required
                />
                {isOwner && (
                  <span className="org-settings-panel__hint">
                    Lowercase letters, numbers, and hyphens. Changing this breaks outstanding invite URLs.
                  </span>
                )}
              </label>

              <label className="org-settings-panel__field">
                <span className="org-settings-panel__label">Logo URL</span>
                <input
                  type="url"
                  className="org-settings-panel__input"
                  value={logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  disabled={!canEdit || saving}
                  placeholder="https://example.com/logo.png"
                />
                {logoUrl.trim() && /^https?:\/\//i.test(logoUrl.trim()) && (
                  <div className="org-settings-panel__logo-preview">
                    <img
                      src={logoUrl.trim()}
                      alt="Logo preview"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </label>

              {canEdit && (
                <div className="org-settings-panel__actions">
                  <button
                    type="button"
                    className="org-settings-panel__secondary"
                    onClick={handleReset}
                    disabled={!dirty || saving}
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="org-settings-panel__primary"
                    disabled={!dirty || saving}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              )}
            </form>

            <section className="org-settings-panel__section">
              <h3 className="org-settings-panel__section-title">Details</h3>
              <dl className="org-settings-panel__details">
                <dt>Plan</dt>
                <dd>
                  {/* Subscription store is the source of truth post-Phase-3;
                      org.plan_id is a denorm and may lag a webhook by a few
                      seconds. Prefer the store value when loaded. */}
                  <span className={`org-settings-panel__badge org-settings-panel__badge--${subscription?.plan_id ?? org.plan_id ?? 'free'}`}>
                    {capitalize(subscription?.plan_id ?? org.plan_id ?? 'free')}
                  </span>
                  {subscription?.cancel_at_period_end && (
                    <span className="org-settings-panel__hint" style={{ marginLeft: 6 }}>
                      cancels {formatDate(subscription.current_period_end)}
                    </span>
                  )}
                </dd>
                <dt>Status</dt>
                <dd>{capitalize(subscription?.status ?? org.status ?? 'active')}</dd>
                <dt>Members</dt>
                <dd>{memberCount ?? '—'}</dd>
                <dt>Created</dt>
                <dd>{formatDate(org.created_at)}</dd>
                <dt>Your role</dt>
                <dd>{capitalize(currentOrg.role)}</dd>
              </dl>
            </section>

            {canBill && (
              <section className="org-settings-panel__section">
                <h3 className="org-settings-panel__section-title">Billing</h3>
                {billingError && (
                  <div className="org-settings-panel__alert org-settings-panel__alert--error">
                    {billingError}
                  </div>
                )}
                <div className="org-settings-panel__actions">
                  {subscription?.has_billing_customer ? (
                    <button
                      type="button"
                      className="org-settings-panel__primary"
                      onClick={handleManageSubscription}
                      disabled={billingBusy}
                    >
                      {billingBusy ? 'Opening…' : 'Manage subscription'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="org-settings-panel__primary"
                      onClick={handleUpgrade}
                      disabled={billingBusy}
                    >
                      {billingBusy ? 'Opening checkout…' : 'Upgrade to Professional'}
                    </button>
                  )}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function pushOrgUpdateToAuthStore(updated: OrgRecord) {
  const state = useAuthStore.getState();
  const next: OrgSummary | null = state.currentOrg
    ? { ...state.currentOrg, name: updated.name, slug: updated.slug }
    : state.currentOrg;
  const orgs = state.orgs.map((o) =>
    o.id === updated.id ? { ...o, name: updated.name, slug: updated.slug } : o,
  );
  useAuthStore.setState({ currentOrg: next, orgs });
}

function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function messageFromError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
